import {Address, TripEnd, TripRow} from '../../interfaces/trip';

export const KM_MAX = 9999;

// A trip being added or edited: km stay the typed text until saved.
export interface TripDraft extends Omit<TripRow, 'km'> {
  km: string[];
  return: boolean;
}

// Translation keys from validate(), or the server's own message after a refused save.
export type DraftErrors = Partial<Record<'from' | 'to' | 'project' | 'km' | 'row', string>>;

// Comma or dot decimal, rounded to 0.1 like TripController::storeRow. null for an empty
// field, NaN for anything the server would refuse.
export function parseKm(raw: string | number | null | undefined): number | null {
  const text = String(raw ?? '').trim().replace(',', '.');
  if (text === '') { return null; }
  if (!/^(\d+(\.\d*)?|\.\d+)$/.test(text)) { return NaN; }
  const km = Math.round(+text * 10) / 10;
  return km > KM_MAX ? NaN : km;
}

export function formatKm(km: number | null, lang: string): string {
  return km == null ? '' : km.toLocaleString(lang === 'nl' ? 'nl-NL' : 'en-GB', {maximumFractionDigits: 1, useGrouping: false});
}

// storeRow saves the return route with the same km, so it doubles days and km.
export function draftPreview(draft: TripDraft): {routes: number, days: number, km: number} {
  const routes = draft.return ? 2 : 1;
  const days = draft.km.map(parseKm).filter(km => km > 0);
  return {routes, days: days.length * routes, km: Math.round(days.reduce((sum, km) => sum + km, 0) * routes * 10) / 10};
}

export function validate(draft: TripDraft): DraftErrors {
  const errors: DraftErrors = {};
  for (const side of ['from', 'to'] as const) {
    const end = draft[side];
    if (!end.home && ![end.street, end.number, end.postcode, end.town].every(part => part?.trim())) {
      errors[side] = 'tracking.trips.err_address';
    }
  }
  if (draft.purpose !== 'commute' && draft.Project_id == null) { errors.project = 'tracking.trips.err_project'; }
  // Edits never carry km (updateRow ignores them): the grid cells change km one day at a time.
  if (!draft.row_id) {
    const days = draft.km.map(parseKm);
    if (days.some(km => Number.isNaN(km))) { errors.km = 'tracking.trips.err_km_range'; }
    else if (!days.some(km => km > 0)) { errors.km = 'tracking.trips.err_km_none'; }
  }
  return errors;
}

export function toRow(draft: TripDraft): TripRow & {return: boolean} {
  return {...draft, km: draft.km.map(parseKm)};
}

// APIService.errorify toasts and rethrows the HttpErrorResponse; a 422 keeps its field errors.
export function serverErrors(e: any): DraftErrors {
  const fields: Record<string, string[]> = e?.error?.errors;
  if (!fields) { return e?.error?.message ? {row: e.error.message} : {}; }
  const errors: DraftErrors = {};
  for (const [key, messages] of Object.entries(fields)) {
    const root = key.split('.')[0];
    const slot = root === 'Project_id' ? 'project' : ['from', 'to', 'km'].includes(root) ? root as keyof DraftErrors : 'row';
    errors[slot] ??= messages[0];
  }
  return errors;
}

// The card a row is drawn in: Commute, one per project, then business trips without one.
export function groupKey(row: Pick<TripRow, 'purpose' | 'Project_id'>): string {
  return row.purpose === 'commute' ? 'commute' : row.Project_id ? `p${row.Project_id}` : 'other';
}

const norm = (v: unknown) => String(v ?? '').trim().toLowerCase();
const sameEnd = (a: TripEnd, b: TripEnd) => !!a.home === !!b.home
  && (['street', 'number', 'postcode', 'town'] as const).every(k => norm(a[k]) === norm(b[k]));

// The row storeRow put a new route on, compared like TripController::routeKey (every field, trimmed,
// case-blind), to find it again after a save. A ticked Home end is saved with the profile address
// (rowFields), so the draft's is filled from it first.
export function sameRoute(row: TripRow, draft: Pick<TripRow, 'purpose' | 'Project_id' | 'description' | 'from' | 'to'>,
                          home: Partial<Address> | null): boolean {
  const saved = (end: TripEnd): TripEnd => end.home ? {...home, home: true} : end;
  return row.purpose === draft.purpose && (row.Project_id ?? null) === (draft.Project_id ?? null)
    && norm(row.description) === norm(draft.description) && sameEnd(row.from, saved(draft.from)) && sameEnd(row.to, saved(draft.to));
}
